// src/components/Cuestionario.jsx
import React, { useState, useEffect, useMemo } from "react";
import ReceptorEjercicio from "../models/ReceptorEjercicio";
import "../styles/Cuestionario.css";
import Lottie from "lottie-react";
import Swal from "sweetalert2";
import successAnim from "../assets/lotties/friendly/goodCheck.json";
import frankito from "../assets/lotties/friendly/frankito.json";
import seeMore from "../assets/lotties/friendly/see-more.json";
import errorAnim from "../assets/lotties/worst/badcheck.json";
import ReporteCalificacion from "./ReporteCalificacion";
import { saveUserGrade } from "../services/userService";
import { actualizarCacheLocal } from "../services/userService";
import { getUserDataCached } from "../services/userService";
import { showLoadingAlert } from "../utils/alerts";
import { closeLoadingAlert } from "../utils/alerts";

// 🎉 Frases positivas
const frasesPositivas = [
  "¡Excelente trabajo, lo lograste! 🌟👏",
  "Me has sorprendido gratamente 😃✨",
  "¡Correcto, sigue así! ✅💪",
  "Tu esfuerzo está dando frutos 🍎📈",
  "¡Muy bien, respuesta impecable! 🏆🎯",
  "Has demostrado gran conocimiento 📚🧠",
  "¡Perfecto, continúa con esa energía! ⚡🔥",
  "¡Correcto, vas por buen camino! 🚀✅",
  "¡Fantástico, lo resolviste muy bien! 🎉🙌",
  "¡Bien hecho, sigue avanzando! 🏅➡️",
  "¡Tu respuesta es brillante! 💡🌟",
  "¡Correcto, estás aprendiendo rápido! 🏃‍♂️📘",
  "¡Excelente, tu lógica es precisa! 🔍🧩",
  "¡Muy bien, lo entendiste a la perfección! 🎯👌",
  "¡Correcto, tu concentración es admirable! 🧘‍♂️✨",
  "¡Gran acierto, felicidades! 🎊🥳",
  "¡Correcto, tu progreso es evidente! 📈🌟",
  "¡Muy bien, estás dominando el tema! 🧠💪",
  "¡Correcto, tu dedicación se nota! 🔥📚",
  "¡Excelente, sigue con esa actitud positiva! 🌞🙌"
];

// 💡 Frases negativas
const frasesNegativas = [
  "No pasa nada, inténtalo de nuevo 🔄🙂",
  "¡Ánimo, cada error es aprendizaje! 💡💪",
  "Te estás acercando, sigue intentando 🚶‍♂️➡️",
  "No te preocupes, lo importante es aprender 📘❤️",
  "¡Casi lo logras, sigue practicando! 🏃‍♂️✨",
  "Equivocarse es parte del proceso 🔄🧠",
  "¡No te desanimes, vas muy bien! 🌈🙂",
  "Cada intento te acerca más a la respuesta 🛤️🎯",
  "¡Tranquilo, la próxima será mejor! 🌟🙂",
  "Lo importante es que sigas avanzando ➡️📈",
  "¡No te rindas, estás progresando! 💪🔥",
  "Los errores también enseñan 📚🤔",
  "¡Ánimo, tu esfuerzo vale mucho! 💖💡",
  "No te preocupes, sigue practicando 🔄📘",
  "¡Confía en ti, lo lograrás! 🌟🙌",
  "Cada equivocación es una oportunidad 🚪✨",
  "¡No pasa nada, sigue adelante! 🚶‍♂️➡️",
  "Lo importante es que estás aprendiendo 📖🙂",
  "¡Ánimo, la constancia te llevará al éxito! 🏆💪",
  "¡Sigue intentándolo, lo conseguirás! 🔥🚀"
];

const Cuestionario = ({ datosFirebase, colorPrincipal, onFinalizar, setModo,
  user, selectedTema, refrescarDatosUsuario }) => {

  const ejercicio = new ReceptorEjercicio(datosFirebase);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [respuestaSeleccionada, setRespuestaSeleccionada] = useState(null);
  const [estado, setEstado] = useState("pregunta");
  const [fraseFeedback, setFraseFeedback] = useState("");
  const [tiempoRestante, setTiempoRestante] = useState(
    ejercicio.tiempos[currentIndex] || 0
  );
  const [opcionesActuales, setOpcionesActuales] = useState([]);
  //console.log("Color principal recibido en Cuestionario: ", colorPrincipal);
  const estilo = { "--color-principal": colorPrincipal };
  const [puntaje, setPuntaje] = useState(0);

  const porcentajeTiempo = ejercicio.tiempos[currentIndex]
    ? (tiempoRestante / ejercicio.tiempos[currentIndex]) * 100
    : 0;

  let colorBarra = "var(--color-principal)";
  if (porcentajeTiempo > 60) {
    colorBarra = "#27ae60"; // verde
  } else if (porcentajeTiempo > 30) {
    colorBarra = "#f1c40f"; // amarillo
  } else {
    colorBarra = "#e74c3c"; // rojo
  }


  const opciones = useMemo(() => ejercicio.opcionesConRespuesta(currentIndex),
    [ejercicio, currentIndex]);

  const pregunta = ejercicio.preguntas[currentIndex];
  //const opciones = ejercicio.opcionesConRespuesta(currentIndex);
  const correcta = ejercicio.respuestasCorrectas[currentIndex];
  const justificacion = ejercicio.justificacion(currentIndex);
  const imagenPregunta = ejercicio.imagenPregunta(currentIndex);
  useEffect(() => {
    if (estado !== "pregunta") return;

    const opciones = ejercicio.opcionesConRespuesta(currentIndex);
    setOpcionesActuales(opciones);
    setTiempoRestante(ejercicio.tiempos[currentIndex] || 0); // reinicia tiempo al cambiar pregunta
    const intervalo = setInterval(() => {
      setTiempoRestante((prev) => {
        if (prev <= 1) {
          clearInterval(intervalo);
          comprobarRespuesta(); // si se acaba el tiempo, se valida automáticamente
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalo);
  }, [currentIndex, estado]);

  useEffect(() => {
    // Avisamos al padre que estamos en modo cuestionario
    setModo("cuestionario");

    // Al desmontar, regresamos a modo normal
    return () => setModo("dashboard");
  }, [setModo]);


  const comprobarRespuesta = () => {
    if (!respuestaSeleccionada) {
      setFraseFeedback("⏰ Tiempo agotado o no seleccionaste opción.");
      setEstado("feedback");
      return;
    }
    const esCorrecta = respuestaSeleccionada === correcta;
    if (esCorrecta) setPuntaje(prev => prev + 1);
    setEstado("feedback");
  };

  const continuar = () => {
    if (currentIndex + 1 < ejercicio.noPreguntas) {
      setCurrentIndex(currentIndex + 1);
      setRespuestaSeleccionada(null);
      setEstado("pregunta");
      setFraseFeedback("");
    } else {
      setEstado("reporte");
      //alert("Cuestionario finalizado 🎉");
      /*if (typeof onFinalizar === "function") {
        onFinalizar();
      }*/
    }
  };

  const esURL = (texto) => {
    try {
      new URL(texto);
      return true;
    } catch {
      return false;
    }
  };


  const verSolucion = () => {
    setEstado("solucion");
  };

  if (estado === "pregunta") {
    return (
      <div className="cuestionario" style={estilo}>

        {/* Cronómetro */}
        <div className="cronometro">
          ⏱ Tiempo restante: {tiempoRestante} s
        </div>

        <div className="barra-tiempo">
          <div
            className="barra-tiempo-fill"
            style={{ width: `${porcentajeTiempo}%`, background: colorBarra }}
          ></div>
        </div>

        <div className="d-flex flex-row justify-content-evenly">
          <h4>Pregunta #{currentIndex + 1}</h4>
          <h4 className="text-success fw-bold">Aciertos {puntaje}</h4>
        </div>

        <p className="mb-2 text-center">{pregunta}</p>
        {imagenPregunta && imagenPregunta !== "0" && (
          <div className="imagen-pregunta">
            <img src={imagenPregunta} alt={`Imagen de la pregunta ${currentIndex + 1}`} />
          </div>
        )}
        <ul className="mt-4">
          {opcionesActuales.map((op, i) => (
            <li key={i}>
              <button
                onClick={() => setRespuestaSeleccionada(op)}
                className={respuestaSeleccionada === op ? "seleccionada" : ""}>
                {op}
              </button>
            </li>
          ))}
        </ul>
        <button
          className="btn-primary"
          onClick={comprobarRespuesta}
          disabled={!respuestaSeleccionada}
        >
          Comprobar
        </button>
      </div>
    );
  }

  if (estado === "feedback") {
    const esCorrecta = respuestaSeleccionada === correcta;
    return (
      <div className="feedback-container" style={estilo}>
        {/* Animación */}
        <div className="feedback-lottie">
          <Lottie
            animationData={esCorrecta ? successAnim : errorAnim}
            loop={false}
            style={{ height: 120 }}
          />
        </div>

        {/* Texto */}
        <h4>{esCorrecta ? "¡Correcto!" : "Incorrecto"}</h4>
        <p className="frase">{fraseFeedback}</p>
        <p className="respuesta-correcta">
          La respuesta correcta es: <strong>{correcta}</strong>
        </p>

        {/* Botones */}
        <div className="btn-group">
          <button className="btn-primary-feedback" onClick={continuar}>
            Continuar
          </button>
          {justificacion && justificacion !== "0" && (
            <button className="btn-secondary-feedback" onClick={verSolucion}>
              Solución
            </button>
          )}
        </div>
      </div>
    );
  }

  if (estado === "solucion") {
    const esRecursoURL = justificacion && esURL(justificacion);
    return (
      <div className="justificacion-container">
        <h2>Justificación</h2>
        {/* Si es texto normal, mostramos el Lottie + texto */}
        {!esRecursoURL && justificacion && (
          <div className="justificacion-texto">
            <Lottie
              animationData={frankito}
              loop={true}
              style={{ height: 140 }}
            />
            <p>{justificacion}</p>
          </div>
        )}

        {/* Si es URL, mostramos el recurso */}
        {esRecursoURL && (
          <div className="justificacion-recurso">
            <Lottie
              animationData={seeMore}
              loop={true}
              style={{ height: 180, marginBottom: "5px" }}
            />
            <a href={justificacion} style={{ marginBottom: "5px" }} target="_blank" rel="noopener noreferrer">
              Ver recurso asociado
            </a>
          </div>
        )}
        <div className="mt-4">
          <button onClick={continuar}>Continuar</button>
        </div>
      </div>
    );
  }

  if (estado === "reporte") {
    return (
      <ReporteCalificacion
        puntaje={puntaje}
        onContinuar={async () => {
          showLoadingAlert();
          try {
            await saveUserGrade(user.uid, selectedTema.codigo, puntaje);
            actualizarCacheLocal(user.uid, selectedTema.codigo, puntaje); // actualiza caché local

            if (typeof refrescarDatosUsuario === "function") {
              await refrescarDatosUsuario();
            }
            closeLoadingAlert();
            if (typeof onFinalizar === "function") {
              onFinalizar();
            }
            Swal.fire("Éxito", "Tu calificación se guardó correctamente.", "success");
          } catch (error) {
            closeLoadingAlert();
            Swal.fire("Error", "No se pudo guardar la calificación.", "error");
          }
        }}
        colorPrincipal={colorPrincipal}
      />
    );
  }

};

export default Cuestionario;
